import { useState } from 'react';
import { SortingProps } from '../paginated-table/paginated-table';

type Pagination<T> = {
  pagination?: { offset?: number; limit: number };
  page: number;
  sorting?: SortingProps<Partial<T>>;
  totalPages: (totalCount: number | undefined | null) => number;
  loadData: (
    sorting: SortingProps<Partial<T>> | undefined,
    page: number
  ) => void;
  reset: () => void;
};

export function usePagination<T>(
  options: {
    itemsPerPage: number;
    defaultSorting?: SortingProps<Partial<T>>;
  } = { itemsPerPage: 10 }
): Pagination<T> {
  const [pagination, setPagination] = useState<
    { offset?: number; limit: number } | undefined
  >({
    offset: 0,
    limit: options.itemsPerPage
  });
  const [sorting, setSorting] = useState<SortingProps<Partial<T>> | undefined>(
    options?.defaultSorting ?? undefined
  );
  const [page, setPage] = useState<number>(1);

  function loadData(
    sorting: SortingProps<Partial<T>> | undefined,
    page: number
  ): void {
    setPage(page);
    setSorting(sorting);
    setPagination({
      offset: (page - 1) * options.itemsPerPage,
      limit: options.itemsPerPage
    });
  }

  function totalPages(totalCount: number | undefined | null): number {
    const c = !totalCount || isNaN(totalCount) ? 0 : totalCount;
    return (
      (c - (c % options.itemsPerPage)) / options.itemsPerPage +
      (c % options.itemsPerPage >= 1 ? 1 : 0)
    );
  }

  function reset(): void {
    setPage(1);
    setPagination({
      offset: 0,
      limit: options.itemsPerPage
    });
  }

  return {
    pagination,
    page,
    sorting,
    totalPages,
    loadData,
    reset
  };
}
